(() => {
  'use strict';

  class TruncatableContent extends HTMLElement {
    constructor() {
      super();
      this.renderFrame = null;
      this.resizeObserver = null;
      this.mutationObserver = null;
      this.expandClickHandler = this.expandClickHandler.bind(this);
      this.shrinkClickHandler = this.shrinkClickHandler.bind(this);
      this.scheduleRender = this.scheduleRender.bind(this);
    }

    connectedCallback() {
      this.truncatable = this.querySelector('.truncatable-content');
      this.expand = this.querySelector('.expand');
      this.shrink = this.querySelector('.shrink');

      if (!this.truncatable || !this.expand || !this.shrink) return;

      this.expand.addEventListener('click', this.expandClickHandler);
      this.shrink.addEventListener('click', this.shrinkClickHandler);
      window.addEventListener('resize', this.scheduleRender, { passive: true });
      window.addEventListener('load', this.scheduleRender, { once: true });

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.scheduleRender);
        this.resizeObserver.observe(this);
        Array.from(this.truncatable.children).forEach((child) => {
          this.resizeObserver.observe(child);
        });
      }

      if ('MutationObserver' in window) {
        this.mutationObserver = new MutationObserver(this.scheduleRender);
        this.mutationObserver.observe(this.truncatable, {
          childList: true,
          subtree: true,
          characterData: true,
        });
      }

      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(this.scheduleRender);
      }

      this.scheduleRender();
    }

    scheduleRender() {
      if (this.renderFrame) cancelAnimationFrame(this.renderFrame);
      this.renderFrame = requestAnimationFrame(() => {
        this.renderFrame = null;
        this.handleRenderLogic();
      });
    }

    handleRenderLogic() {
      if (!this.isConnected || !this.truncatable) return;

      const isExpanded = this.classList.contains('expanded');

      if (isExpanded) {
        this.truncatable.style.maxHeight = `${this.truncatable.scrollHeight}px`;
        this.classList.add('truncated');
        return;
      }

      const isOverflowing =
        this.truncatable.scrollHeight > this.truncatable.clientHeight + 1 ||
        this.truncatable.scrollWidth > this.truncatable.clientWidth + 1;

      this.classList.toggle('truncated', isOverflowing);
    }

    expandClickHandler(event) {
      event.preventDefault();
      event.stopPropagation();
      this.truncatable.style.maxHeight = `${this.truncatable.scrollHeight}px`;
      this.classList.add('expanded', 'truncated');
      this.expand.setAttribute('aria-expanded', 'true');
    }

    shrinkClickHandler(event) {
      event.preventDefault();
      event.stopPropagation();
      this.truncatable.style.removeProperty('max-height');
      this.classList.remove('expanded');
      this.expand.setAttribute('aria-expanded', 'false');
      this.scheduleRender();
    }

    disconnectedCallback() {
      if (this.renderFrame) cancelAnimationFrame(this.renderFrame);
      window.removeEventListener('resize', this.scheduleRender);
      window.removeEventListener('load', this.scheduleRender);
      this.resizeObserver?.disconnect();
      this.mutationObserver?.disconnect();
      this.expand?.removeEventListener('click', this.expandClickHandler);
      this.shrink?.removeEventListener('click', this.shrinkClickHandler);
    }
  }

  if (!customElements.get('truncatable-content')) {
    customElements.define('truncatable-content', TruncatableContent);
  }
})();
